import clsx from 'clsx';
import type { SplitButtonProps as PrimeSplitButtonProps } from 'primereact/splitbutton';
import { SplitButton as PrimeSplitButton } from 'primereact/splitbutton';
import type { ButtonVariant } from '../button/button';

export interface SplitButtonProps
  extends Omit<PrimeSplitButtonProps, 'text' | 'rounded' | 'raised' | 'outlined' | 'severity' | 'size'> {
  variant?: ButtonVariant;
}

const commonPrimaryButtonStyles =
  'sm:w-auto text-base leading-5 rounded-l-sm focus:outline-hidden justify-center transition-colors duration-200 min-w-[3rem] min-h-[2.25rem] py-2 px-5 font-medium';
const primaryButtonStyles = {
  regular: 'bg-primary-2 hover:bg-primary-3 text-mono-1',
  alternative: 'bg-primary-1 hover:bg-primary-2 text-mono-1',
};

const commonMenuButtonStyles = 'rounded-r py-2 px-4';
const menuButtonStyles = {
  regular: 'bg-primary-2 hover:bg-primary-3 text-mono-1',
  alternative: 'bg-primary-1 hover:bg-primary-2 text-mono-1',
};

const menuStyles = 'shadow-md rounded-sm';

/**
 * A wrapper component for PrimeReact's SplitButton.
 *
 * This component takes all the properties of `PrimeSplitButton` and passes them through.
 *
 * @component
 * @param {SplitButtonProps} props - The properties passed to the SplitButton component.
 * @returns {React.JSX.Element} A JSX element representing the SplitButton.
 *
 * @example
 * ```tsx
 * <SplitButton label="Options" model={items} variant="alternative" />
 * ```
 *
 * @see https://v9.primereact.org/splitbutton/#basic
 */
const SplitButton = ({ variant = 'regular', className, ...rest }: SplitButtonProps) => {
  return (
    <PrimeSplitButton
      {...rest}
      className={clsx('odin', className)}
      buttonClassName={clsx(commonPrimaryButtonStyles, primaryButtonStyles[variant])}
      menuButtonClassName={clsx(commonMenuButtonStyles, menuButtonStyles[variant])}
      menuClassName={clsx('odin', menuStyles)}
    />
  );
};

export default SplitButton;
